// ═══════════════════════════════════════════════════════════════
// GALLERY SECTION — Carousel تلقائي
// كيفية الاستخدام:
//   1. استبدل قيم src في مصفوفة GALLERY_IMAGES بمسارات صورك
//   2. الصور المقترحة: 1280×800px أو نسبة 16:10
//   3. ضع الصور في مجلد /public/screenshots/ في مشروعك
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback } from "react";

// ── أضف صورك هنا ──────────────────────────────────────────────
const GALLERY_IMAGES = [
    {
        src: "src/screenshots/landing.png",
        title: "الشاشة الرئيسية",
        desc: "لوحة تحكم مع إحصائيات اليوم فوراً",
        tag: "Dashboard",
        color: "#4395A3",
    },
    {
        src: "src/screenshots/pos.png",
        title: "نقطة البيع",
        desc: "كاشير سريع مع بحث فوري وطباعة",
        tag: "POS",
        color: "#f97316",
    },
    {
        src: "src/screenshots/delivery.png",
        title: "طلبات التوصيل",
        desc: "دليفري معلق مع بيانات العميل كاملة",
        tag: "Delivery",
        color: "#3b82f6",
    },
    {
        src: "src/screenshots/profits.png",
        title: "الأرباح والتقارير",
        desc: "رسوم بيانية تفاعلية وتصدير PDF",
        tag: "Profits",
        color: "#22c55e",
    },
    {
        src: "src/screenshots/finance.png",
        title: "المالية الشاملة",
        desc: "نظرة 360° على كل حركاتك المالية",
        tag: "Finance",
        color: "#a855f7",
    },
    {
        src: "src/screenshots/expenses.png",
        title: "المصاريف",
        desc: "تسجيل وتتبع كل مصروف بالتفصيل",
        tag: "Expenses",
        color: "#ef4444",
    },
    {
        src: "src/screenshots/debts.png",
        title: "الديون",
        desc: "متابعة عملاء الدين والتحصيل",
        tag: "Debts",
        color: "#f59e0b",
    },
    {
        src: "src/screenshots/admin.png",
        title: "الإدارة والإعدادات",
        desc: "منتجات، فئات، طباعة، كلمات مرور",
        tag: "Admin",
        color: "#64748b",
    },
];

const SLIDE_INTERVAL = 3500; // ms بين كل شريحة

export default function GallerySection() {
    const [active, setActive]       = useState(0);
    const [prev, setPrev]           = useState(null);
    const [dir, setDir]             = useState(1);     // 1 = next, -1 = prev
    const [animating, setAnimating] = useState(false);
    const [paused, setPaused]       = useState(false);
    const [lbOpen, setLbOpen]       = useState(false);
    const timerRef                  = useRef(null);
    const total                     = GALLERY_IMAGES.length;

    // ── goto ────────────────────────────────────────────────────
    const goto = useCallback((idx, direction = 1) => {
        if (animating) return;
        const next = (idx + total) % total;
        if (next === active) return;
        setPrev(active);
        setDir(direction);
        setAnimating(true);
        setActive(next);
        setTimeout(() => { setPrev(null); setAnimating(false); }, 520);
    }, [active, animating, total]);

    const next = useCallback(() => goto(active + 1, 1),  [active, goto]);
    const back = useCallback(() => goto(active - 1, -1), [active, goto]);

    // ── Auto-play ───────────────────────────────────────────────
    useEffect(() => {
        if (paused) return;
        timerRef.current = setInterval(next, SLIDE_INTERVAL);
        return () => clearInterval(timerRef.current);
    }, [paused, next]);

    // ── Keyboard ────────────────────────────────────────────────
    useEffect(() => {
        const fn = (e) => {
            if (lbOpen) {
                if (e.key === "Escape")     { setLbOpen(false); }
                if (e.key === "ArrowLeft")  { next(); }
                if (e.key === "ArrowRight") { back(); }
            }
        };
        window.addEventListener("keydown", fn);
        return () => window.removeEventListener("keydown", fn);
    }, [lbOpen, next, back]);

    const cur  = GALLERY_IMAGES[active];
    const prv  = prev !== null ? GALLERY_IMAGES[prev] : null;

    // ── Slide animation values ──────────────────────────────────
    // entering slide
    const enterFrom  = dir === 1 ? "100%"  : "-100%";
    const exitTo     = dir === 1 ? "-100%" : "100%";

    return (
        <>
            <style>{`
        @keyframes slideEnter {
          from { transform: translateX(var(--enter-from)); opacity: 0.6; }
          to   { transform: translateX(0);                 opacity: 1;   }
        }
        @keyframes slideExit {
          from { transform: translateX(0);               opacity: 1;   }
          to   { transform: translateX(var(--exit-to));  opacity: 0;   }
        }
        @keyframes gBlobFloat1 { 0%,100%{transform:translate(0,0) scale(1)} 45%{transform:translate(30px,-20px) scale(1.05)} }
        @keyframes gBlobFloat2 { 0%,100%{transform:translate(0,0) scale(1)} 40%{transform:translate(-25px,18px) scale(1.04)} }
        @keyframes gDotPulse    { 0%,100%{opacity:.55;transform:scale(1)} 50%{opacity:1;transform:scale(1.45)} }
        @keyframes gLbIn        { from{opacity:0;transform:scale(0.96)} to{opacity:1;transform:scale(1)} }
        @keyframes gThumbIn     { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes gProgressBar {
          from { width: 0%; }
          to   { width: 100%; }
        }
        .g-nav-btn:hover { transform: scale(1.1) !important; }
        .g-nav-btn:active { transform: scale(0.95) !important; }
        .g-thumb:hover { transform: scale(1.08) translateY(-3px) !important; }
      `}</style>

            {/* ══ SECTION ══════════════════════════════════════════ */}
            <section
                id="gallery"
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
                style={{
                    padding: "96px 0",
                    background: "linear-gradient(180deg, #080c18 0%, #0d1117 50%, #070b15 100%)",
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                {/* ── BG decoration ── */}
                <div style={{ position:"absolute", inset:0, pointerEvents:"none" }}>
                    <div style={{
                        position:"absolute", top:"-15%", right:"-12%",
                        width:650, height:650, borderRadius:"50%",
                        background:`radial-gradient(circle,${cur.color}14 0%,transparent 65%)`,
                        animation:"gBlobFloat1 20s ease-in-out infinite",
                        transition:"background 0.8s ease",
                    }}/>
                    <div style={{
                        position:"absolute", bottom:"-20%", left:"-10%",
                        width:700, height:700, borderRadius:"50%",
                        background:"radial-gradient(circle,rgba(67,149,163,0.07) 0%,transparent 65%)",
                        animation:"gBlobFloat2 26s ease-in-out infinite",
                    }}/>
                    <div style={{
                        position:"absolute", inset:0,
                        backgroundImage:"radial-gradient(circle,rgba(255,255,255,0.045) 1px,transparent 1px)",
                        backgroundSize:"38px 38px",
                    }}/>
                </div>

                <div style={{ maxWidth:1200, margin:"0 auto", padding:"0 32px", position:"relative", zIndex:1 }}>

                    {/* ── Header ── */}
                    <div style={{ textAlign:"center", marginBottom:52, direction:"rtl" }}>
                        <div style={{
                            display:"inline-flex", alignItems:"center", gap:8,
                            background:"rgba(67,149,163,0.12)", border:"1px solid rgba(67,149,163,0.25)",
                            borderRadius:40, padding:"6px 18px", marginBottom:16,
                        }}>
                            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#7dd3dc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                                <polyline points="21 15 16 10 5 21"/>
                            </svg>
                            <span style={{ fontSize:11.5, fontWeight:900, color:"#7dd3dc", letterSpacing:1 }}>معرض الشاشات</span>
                        </div>
                        <h2 style={{ fontSize:"clamp(26px,3.5vw,42px)", fontWeight:900, color:"white", marginBottom:10 }}>
                            شاهد النظام بنفسك
                        </h2>
                        <p style={{ fontSize:14.5, color:"rgba(255,255,255,0.42)", fontWeight:600, maxWidth:480, margin:"0 auto" }}>
                            جولة كاملة في كل شاشات النظام — يتقدم تلقائياً أو تحكم يدوياً
                        </p>
                    </div>

                    {/* ══ MAIN STAGE ════════════════════════════════════ */}
                    <div style={{
                        position:"relative",
                        borderRadius:28,
                        overflow:"hidden",
                        boxShadow:`0 0 0 1px rgba(255,255,255,0.07), 0 40px 100px rgba(0,0,0,0.7), 0 0 80px ${cur.color}18`,
                        transition:"box-shadow 0.8s ease",
                        background:"#090e1a",
                        cursor: "pointer",
                    }}
                         onClick={() => setLbOpen(true)}
                    >
                        {/* ── Window chrome bar ── */}
                        <div style={{
                            padding:"11px 18px",
                            background:"rgba(255,255,255,0.04)",
                            borderBottom:"1px solid rgba(255,255,255,0.06)",
                            display:"flex", alignItems:"center", gap:7,
                        }}>
                            {["#ff5f56","#ffbd2e","#27c93f"].map(c => (
                                <span key={c} style={{ width:11, height:11, borderRadius:"50%", background:c, flexShrink:0 }}/>
                            ))}
                            {/* URL bar */}
                            <div style={{
                                flex:1, margin:"0 16px",
                                background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.07)",
                                borderRadius:7, padding:"4px 12px",
                                fontSize:11, color:"rgba(255,255,255,0.3)", fontWeight:700,
                                display:"flex", alignItems:"center", gap:6,
                            }}>
                                <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                                نظام النقطة — {cur.title}
                            </div>
                            {/* Tag badge */}
                            <div style={{
                                padding:"3px 11px", borderRadius:20,
                                background:`${cur.color}22`, border:`1px solid ${cur.color}40`,
                                fontSize:10, fontWeight:900, color:cur.color,
                                transition:"all 0.4s ease",
                            }}>{cur.tag}</div>
                        </div>

                        {/* ── Slide area ── */}
                        <div style={{ position:"relative", aspectRatio:"16/9", overflow:"hidden" }}>

                            {/* Exiting slide */}
                            {prv && animating && (
                                <div style={{
                                    position:"absolute", inset:0,
                                    "--exit-to": exitTo,
                                    animation:"slideExit 0.5s cubic-bezier(0.4,0,0.2,1) forwards",
                                }}>
                                    <SlideContent img={prv} />
                                </div>
                            )}

                            {/* Active slide */}
                            <div style={{
                                position:"absolute", inset:0,
                                "--enter-from": animating ? enterFrom : "0%",
                                animation: animating ? "slideEnter 0.5s cubic-bezier(0.4,0,0.2,1) forwards" : "none",
                            }}>
                                <SlideContent img={cur} />
                            </div>

                            {/* ── Prev / Next overlay buttons ── */}
                            <button className="g-nav-btn" onClick={e => { e.stopPropagation(); back(); }} style={{
                                position:"absolute", right:18, top:"50%", transform:"translateY(-50%)",
                                width:46, height:46, borderRadius:"50%",
                                background:"rgba(0,0,0,0.55)", backdropFilter:"blur(10px)",
                                border:"1px solid rgba(255,255,255,0.14)", color:"white",
                                cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
                                transition:"transform 0.18s ease, background 0.2s",
                                zIndex:10,
                            }}>
                                <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                            </button>

                            <button className="g-nav-btn" onClick={e => { e.stopPropagation(); next(); }} style={{
                                position:"absolute", left:18, top:"50%", transform:"translateY(-50%)",
                                width:46, height:46, borderRadius:"50%",
                                background:"rgba(0,0,0,0.55)", backdropFilter:"blur(10px)",
                                border:"1px solid rgba(255,255,255,0.14)", color:"white",
                                cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
                                transition:"transform 0.18s ease, background 0.2s",
                                zIndex:10,
                            }}>
                                <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                            </button>

                            {/* ── Caption overlay ── */}
                            <div style={{
                                position:"absolute", bottom:0, left:0, right:0,
                                background:"linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 60%, transparent 100%)",
                                padding:"32px 24px 20px", direction:"rtl",
                            }}>
                                <div style={{ fontSize:18, fontWeight:900, color:"white", marginBottom:5 }}>{cur.title}</div>
                                <div style={{ fontSize:13, fontWeight:600, color:"rgba(255,255,255,0.6)" }}>{cur.desc}</div>
                            </div>

                            {/* ── Expand hint ── */}
                            <div style={{
                                position:"absolute", top:14, left:14,
                                background:"rgba(0,0,0,0.6)", backdropFilter:"blur(8px)",
                                border:"1px solid rgba(255,255,255,0.1)", borderRadius:10,
                                padding:"5px 11px", fontSize:11, fontWeight:800, color:"rgba(255,255,255,0.5)",
                                display:"flex", alignItems:"center", gap:5,
                            }}>
                                <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>
                                عرض كامل
                            </div>
                        </div>

                        {/* ── Progress bar ── */}
                        <div style={{ height:3, background:"rgba(255,255,255,0.06)" }}>
                            <div
                                key={`${active}-${paused}`}
                                style={{
                                    height:"100%",
                                    background:`linear-gradient(90deg, ${cur.color}, ${cur.color}aa)`,
                                    borderRadius:2,
                                    animation: paused ? "none" : `gProgressBar ${SLIDE_INTERVAL}ms linear forwards`,
                                }}
                            />
                        </div>
                    </div>

                    {/* ══ THUMBNAILS ════════════════════════════════════ */}
                    <div style={{
                        display:"flex", gap:10, marginTop:20,
                        overflowX:"auto", padding:"4px 0 8px",
                        scrollbarWidth:"none",
                        justifyContent: total <= 6 ? "center" : "flex-start",
                    }}>
                        {GALLERY_IMAGES.map((img, i) => (
                            <button
                                key={i}
                                className="g-thumb"
                                onClick={() => goto(i, i > active ? 1 : -1)}
                                style={{
                                    flexShrink:0,
                                    width:110, height:68, borderRadius:12, overflow:"hidden",
                                    border:`2px solid ${i === active ? img.color : "rgba(255,255,255,0.08)"}`,
                                    boxShadow: i === active ? `0 0 0 3px ${img.color}30, 0 8px 24px rgba(0,0,0,0.4)` : "none",
                                    cursor:"pointer", position:"relative",
                                    background: `linear-gradient(135deg,${img.color}22,${img.color}08)`,
                                    transition:"all 0.25s cubic-bezier(0.34,1.56,0.64,1)",
                                    transform: i === active ? "scale(1.06) translateY(-3px)" : "scale(1)",
                                    animation:`gThumbIn 0.4s ease ${i * 40}ms both`,
                                }}
                            >
                                {/* Image or placeholder */}
                                {img.src
                                    ? <img src={img.src} alt={img.title} style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                                    : <div style={{
                                        width:"100%", height:"100%",
                                        display:"flex", flexDirection:"column",
                                        alignItems:"center", justifyContent:"center",
                                        gap:3,
                                    }}>
                                        <div style={{ fontSize:16, opacity: i === active ? 0.9 : 0.45 }}>📸</div>
                                        <div style={{ fontSize:8.5, fontWeight:900, color: i === active ? img.color : "rgba(255,255,255,0.35)" }}>
                                            {img.tag}
                                        </div>
                                    </div>
                                }
                                {/* Active dot */}
                                {i === active && (
                                    <div style={{
                                        position:"absolute", bottom:5, right:5,
                                        width:6, height:6, borderRadius:"50%",
                                        background:img.color,
                                        boxShadow:`0 0 8px ${img.color}`,
                                        animation:"gDotPulse 1.5s ease-in-out infinite",
                                    }}/>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* ── Dot indicators ── */}
                    <div style={{ display:"flex", justifyContent:"center", gap:7, marginTop:18 }}>
                        {GALLERY_IMAGES.map((img, i) => (
                            <button
                                key={i}
                                onClick={() => goto(i, i > active ? 1 : -1)}
                                style={{
                                    width: i === active ? 28 : 7,
                                    height:7, borderRadius:4,
                                    background: i === active ? cur.color : "rgba(255,255,255,0.18)",
                                    border:"none", cursor:"pointer", padding:0,
                                    transition:"all 0.35s cubic-bezier(0.34,1.56,0.64,1)",
                                    boxShadow: i === active ? `0 0 10px ${cur.color}80` : "none",
                                }}
                            />
                        ))}
                    </div>

                    {/* ── Counter ── */}
                    <div style={{ textAlign:"center", marginTop:14, fontSize:12, fontWeight:700, color:"rgba(255,255,255,0.25)", direction:"rtl" }}>
                        {active + 1} / {total} &nbsp;•&nbsp; {paused ? "⏸ متوقف" : "▶ تشغيل تلقائي"}
                    </div>
                </div>
            </section>

            {/* ══ LIGHTBOX ══════════════════════════════════════════ */}
            {lbOpen && (
                <div
                    onClick={() => setLbOpen(false)}
                    style={{
                        position:"fixed", inset:0, zIndex:9999,
                        background:"rgba(0,0,0,0.94)", backdropFilter:"blur(20px)",
                        display:"flex", alignItems:"center", justifyContent:"center",
                        animation:"gLbIn 0.25s ease both",
                    }}
                >
                    <div onClick={e => e.stopPropagation()} style={{ maxWidth:1100, width:"92%", position:"relative" }}>

                        {/* Close */}
                        <button onClick={() => setLbOpen(false)} style={{
                            position:"absolute", top:-52, right:0,
                            width:40, height:40, borderRadius:"50%",
                            background:"rgba(255,255,255,0.1)", border:"1px solid rgba(255,255,255,0.18)",
                            color:"white", cursor:"pointer",
                            display:"flex", alignItems:"center", justifyContent:"center",
                            transition:"background 0.2s",
                        }}
                                onMouseEnter={e => e.currentTarget.style.background="rgba(255,255,255,0.2)"}
                                onMouseLeave={e => e.currentTarget.style.background="rgba(255,255,255,0.1)"}
                        >
                            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>

                        {/* Prev */}
                        <button onClick={back} style={{
                            position:"absolute", right:-60, top:"50%", transform:"translateY(-50%)",
                            width:48, height:48, borderRadius:"50%",
                            background:"rgba(255,255,255,0.1)", border:"1px solid rgba(255,255,255,0.15)",
                            color:"white", cursor:"pointer",
                            display:"flex", alignItems:"center", justifyContent:"center",
                            transition:"all 0.2s",
                        }}
                                onMouseEnter={e => { e.currentTarget.style.background="rgba(255,255,255,0.2)"; e.currentTarget.style.transform="translateY(-50%) scale(1.1)"; }}
                                onMouseLeave={e => { e.currentTarget.style.background="rgba(255,255,255,0.1)"; e.currentTarget.style.transform="translateY(-50%)"; }}
                        >
                            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                        </button>

                        {/* Next */}
                        <button onClick={next} style={{
                            position:"absolute", left:-60, top:"50%", transform:"translateY(-50%)",
                            width:48, height:48, borderRadius:"50%",
                            background:"rgba(255,255,255,0.1)", border:"1px solid rgba(255,255,255,0.15)",
                            color:"white", cursor:"pointer",
                            display:"flex", alignItems:"center", justifyContent:"center",
                            transition:"all 0.2s",
                        }}
                                onMouseEnter={e => { e.currentTarget.style.background="rgba(255,255,255,0.2)"; e.currentTarget.style.transform="translateY(-50%) scale(1.1)"; }}
                                onMouseLeave={e => { e.currentTarget.style.background="rgba(255,255,255,0.1)"; e.currentTarget.style.transform="translateY(-50%)"; }}
                        >
                            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                        </button>

                        {/* Image */}
                        <div style={{
                            borderRadius:20, overflow:"hidden",
                            border:`2px solid ${cur.color}50`,
                            boxShadow:`0 0 80px ${cur.color}28, 0 40px 80px rgba(0,0,0,0.6)`,
                            background:`linear-gradient(135deg,${cur.color}18,rgba(9,14,26,0.95))`,
                            aspectRatio:"16/9",
                            display:"flex", alignItems:"center", justifyContent:"center",
                        }}>
                            {cur.src
                                ? <img src={cur.src} alt={cur.title} style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                                : <div style={{ textAlign:"center", fontFamily:"inherit" }}>
                                    <div style={{ fontSize:64, opacity:0.3, marginBottom:14 }}>📸</div>
                                    <div style={{ fontSize:22, fontWeight:900, color:cur.color }}>{cur.title}</div>
                                    <div style={{ fontSize:13, color:"rgba(255,255,255,0.35)", marginTop:8, fontWeight:600 }}>
                                        ضع لقطة الشاشة هنا
                                    </div>
                                    <code style={{ fontSize:11, color:"rgba(255,255,255,0.2)", marginTop:6, display:"block" }}>
                                        {`src: "/screenshots/${cur.tag.toLowerCase()}.png"`}
                                    </code>
                                </div>
                            }
                        </div>

                        {/* Caption */}
                        <div style={{ textAlign:"center", marginTop:20, direction:"rtl" }}>
                            <div style={{ fontSize:20, fontWeight:900, color:"white", marginBottom:6 }}>{cur.title}</div>
                            <div style={{ fontSize:13.5, color:"rgba(255,255,255,0.45)", fontWeight:600 }}>{cur.desc}</div>
                            <div style={{ fontSize:11.5, color:"rgba(255,255,255,0.22)", marginTop:8, fontWeight:700 }}>
                                {active + 1} / {total} &nbsp;•&nbsp; ← → للتنقل &nbsp;•&nbsp; Esc للإغلاق
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

// ── Slide content (image or styled placeholder) ───────────────
function SlideContent({ img }) {
    return (
        <div style={{
            width:"100%", height:"100%",
            background:`linear-gradient(135deg,${img.color}1a,rgba(9,14,26,0.95))`,
            display:"flex", alignItems:"center", justifyContent:"center",
            position:"relative", overflow:"hidden",
        }}>
            {img.src ? (
                <img
                    src={img.src}
                    alt={img.title}
                    style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}
                />
            ) : (
                // Styled placeholder — يُحذف عند إضافة الصور الحقيقية
                <PlaceholderSlide img={img} />
            )}
        </div>
    );
}

// ── Placeholder visual ────────────────────────────────────────
function PlaceholderSlide({ img }) {
    return (
        <div style={{
            width:"100%", height:"100%",
            display:"flex", flexDirection:"column",
            alignItems:"center", justifyContent:"center",
            gap:16, fontFamily:"inherit",
            position:"relative",
        }}>
            {/* Grid bg */}
            <div style={{
                position:"absolute", inset:0,
                backgroundImage:`radial-gradient(circle,${img.color}20 1px,transparent 1px)`,
                backgroundSize:"32px 32px",
            }}/>
            {/* Glow */}
            <div style={{
                position:"absolute", top:"50%", left:"50%",
                transform:"translate(-50%,-50%)",
                width:300, height:300, borderRadius:"50%",
                background:`radial-gradient(circle,${img.color}18 0%,transparent 70%)`,
            }}/>
            {/* Content */}
            <div style={{ position:"relative", textAlign:"center" }}>
                <div style={{
                    width:80, height:80, borderRadius:28, margin:"0 auto 18px",
                    background:`${img.color}22`, border:`2px solid ${img.color}40`,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:36,
                }}>📸</div>
                <div style={{
                    fontSize:"clamp(18px,2.5vw,28px)", fontWeight:900,
                    color:"white", marginBottom:8,
                }}>{img.title}</div>
                <div style={{
                    fontSize:14, fontWeight:700, color:"rgba(255,255,255,0.4)",
                    marginBottom:20,
                }}>{img.desc}</div>
                <div style={{
                    display:"inline-block",
                    padding:"7px 18px", borderRadius:20,
                    background:`${img.color}20`, border:`1px solid ${img.color}40`,
                    fontSize:12, fontWeight:900, color:img.color,
                    letterSpacing:1,
                }}>
                    {img.tag}
                </div>
            </div>
            {/* Bottom hint */}
            <div style={{
                position:"absolute", bottom:20,
                fontSize:11.5, fontWeight:700, color:"rgba(255,255,255,0.2)",
                direction:"rtl",
            }}>
                {`استبدل src: null بـ "/screenshots/${img.tag.toLowerCase()}.png"`}
            </div>
        </div>
    );
}